import { Modal, Tabs } from '@bfe/moly';
import { ReactNode, useCallback, useEffect } from 'react';
import { getDefaultIndex } from '../../store/config';
import { WalletModalTab, useConnectWalletModal } from '../../store/dialogs';
import { useLocale } from '../../store/i18nMessage';
import { UserWalletStoreState, userWalletStore } from '../../store/userWallet';
import { molyDarkThemeClass } from '../../utils/trick';
import { BybitWalletList } from './BybitWalletList';
import { ThirdWalletList } from './ThirdWalletList';
import { scTrack } from '../../utils/scTrack';

export type Web3ConnectWalletModalProps = {
  // 连接弹窗的标题: ReactNode，默认是通用的：连接钱包 文案。
  title?: ReactNode;
  // 连接弹窗的副标题: ReactNode, 默认是：web3这边的连接钱包文案（不排除我们会随版本更改）  false，代表不展示
  subTitle?: ReactNode;

  // 是否显示弹窗，不传使用全局store的弹窗状态
  visible?: boolean;
  // 显示的tab，不传使用全局的
  activeTab?: WalletModalTab;
  // 当tab改变时的回调，不传就默认改变全局状态
  onTabChange?: (tab: WalletModalTab) => void;
  // 当弹窗关闭，不传就是改的全局状态
  onClose?: () => void;

  // 弹窗初始的tab，依然默认用的全局的
  initialTab?: WalletModalTab;

  // bybit 列表下的一个tip，默认用全局
  bybitWalletTabTip?: ReactNode;
  // 不显示下载app，默认用全局
  noDownLoadApp?: boolean;
  // 不显示bybit 扩展，默认用全局
  noBybitExtension?: boolean;
  // zIndex，默认用全局
  zIndex?: number;

  // bybit 插件版本，默认用全局
  bybitExtVersion?: string;
  // 用户点击连接钱包成功时的回调
  onConnected?: (walletState: UserWalletStoreState) => void;
};

export type Web3ConnectWalletModalUIProps = Omit<Web3ConnectWalletModalProps, 'onTabChange'>;

export function Web3ConnectWalletModal(props: Web3ConnectWalletModalProps) {
  const uiPropsStore = useConnectWalletModal();
  const storeOnTagChange = useCallback((t) => uiPropsStore.setState('activeTab', t), []);
  const { t } = useLocale();

  const {
    title = t('connect_wallet'),
    subTitle,
    noBybitExtension = uiPropsStore.noBybitExtension,
    noDownLoadApp = uiPropsStore.noDownLoadApp,
    bybitWalletTabTip = uiPropsStore.bybitWalletTabTip,
    initialTab = uiPropsStore.initialTab ?? WalletModalTab.bybitWallet,
    visible = uiPropsStore.visible,
    onClose = () => {
      uiPropsStore.hide();
      uiPropsStore.onClose?.();
    },
    onTabChange = storeOnTagChange,
    activeTab = uiPropsStore.activeTab,
    zIndex = getDefaultIndex(),
    onConnected = uiPropsStore.onConnected,
    bybitExtVersion = uiPropsStore.bybitExtVersion,
  } = props;

  useEffect(() => {
    if (!visible) {
      onTabChange?.(initialTab);
    }

    //这里 怕外部的onTabChange 引起重复刷新
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, visible]);

  useEffect(() => {
    if (visible) {
      scTrack('Web3PageView', {
        page_name: 'web3sdkPopupPage',
      });
    }
  }, [visible]);

  const handleOnConnect = () => {
    onConnected?.(userWalletStore.state);
    onClose();
  };

  return (
    <Modal
      open={visible}
      footer={null}
      modal={false}
      width={640}
      onCancel={onClose}
      wrapClassName="web3-connect-wallet-dialog"
      className={molyDarkThemeClass}
      onOpenAutoFocus={(e) => e.preventDefault()}
      zIndex={zIndex}
      title={title}
      subTitle={subTitle ? <div className="px-8">{subTitle}</div> : null}
      contextWrapper="overflow-auto !pr-2 ltr:mr-4 rtl:ml-4 web3-scrollbar"
    >
      <Tabs
        activeKey={activeTab}
        onChange={(e) => {
          onTabChange(e as WalletModalTab);
        }}
        size="medium"
        items={[
          {
            key: WalletModalTab.bybitWallet,
            label: t('wallet_web3'),
            children: (
              <div style={{ minHeight: 365 }}>
                <BybitWalletList
                  onConnect={handleOnConnect}
                  noBybitExtension={noBybitExtension}
                  noDownLoadApp={noDownLoadApp}
                  bybitExtVersion={bybitExtVersion}
                />
                {Boolean(bybitWalletTabTip) && <div>{bybitWalletTabTip}</div>}
              </div>
            ),
          },
          {
            key: WalletModalTab.thirdWallet,
            label: t('wallet_three'),
            children: (
              // 这个高度，刚好三行 3方钱包的连接，固定高度为了切tab时不抖动
              // 但是无法防止完全不抖动，因为多语言会导致上bybit的列表（即第一个tab）高度变化
              <div style={{ height: 365 }}>
                <ThirdWalletList onConnect={handleOnConnect} />
              </div>
            ),
          },
        ]}
      ></Tabs>
    </Modal>
  );
}
